import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Delay showing cookie consent to let users focus on demo first
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 5000); // 5 second delay
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("cookieConsent", "all");
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    localStorage.setItem("cookieConsent", "essential");
    setIsVisible(false);
  };

  const handleLearnMore = () => {
    window.open("/privacy-policy", "_blank");
    // Don't hide the bar - cookie consent stays visible
  };

  const handleClose = () => {
    // Closing without choice = essential only
    localStorage.setItem("cookieConsent", "essential");
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 z-40 pointer-events-none"
        >
          <div className="container max-w-4xl mx-auto pointer-events-auto">
            <div className="relative bg-card border border-border rounded-lg shadow-2xl p-4 md:p-6">
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Content */}
              <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
                {/* Message */}
                <div className="flex-1">
                  <p className="text-sm text-foreground/80 leading-relaxed pr-8">
                    {t("cookieConsent.message")}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 w-full md:w-auto">
                  <Button
                    onClick={handleAcceptAll}
                    size="sm"
                    className="flex-1 md:flex-none"
                  >
                    {t("cookieConsent.acceptAll")}
                  </Button>
                  
                  <Button
                    onClick={handleRejectNonEssential}
                    variant="outline"
                    size="sm"
                    className="flex-1 md:flex-none"
                  >
                    {t("cookieConsent.rejectNonEssential")}
                  </Button>
                  
                  <Button
                    onClick={handleLearnMore}
                    variant="ghost"
                    size="sm"
                    className="flex-1 md:flex-none"
                  >
                    {t("cookieConsent.learnMore")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
